import * as Dialog from '@radix-ui/react-dialog'
import { PanelRight, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { COMMON } from '@brickup/shared/locales/de/common'
import { cn } from './cn'
import { PortalContainerScope } from './portal-container'
import { FOCUS_RING } from './recipes'
import { useEditorialSurface, usePortalThemeClass } from './theme-scope'

export type ModalSize = keyof typeof SIZE

const SIZE = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
  full: 'max-w-[95vw]',
} as const

/**
 * The same widths plus the 20rem an open `aside` takes: the panel is added to
 * the dialog, so the body keeps the width it was being read at. `full` is
 * missing because it is already the viewport cap — there the panel comes out
 * of that width, and the dialog keeps `SIZE.full`.
 *
 * Tailwind reads these strings literally, so each is written out.
 */
const SIZE_WITH_ASIDE: Partial<Record<keyof typeof SIZE, string>> = {
  sm: 'md:max-w-[48rem]',
  md: 'md:max-w-[56rem]',
  lg: 'md:max-w-[68rem]',
  xl: 'md:max-w-[84rem]',
}

/**
 * Where the dialog sits.
 *
 * A `sheet` rises from the bottom edge across the full width, which is what a
 * phone wants: the thumb reaches the foot, and what it covers stays visible
 * above it. From `md` up there is room for the dialog placement and no thumb
 * to reach, so it becomes one — the breakpoint lives here rather than in every
 * caller that wants a sheet on a phone.
 *
 * Both are placed against what is on screen rather than against the page: an
 * on-screen keyboard covers the foot of the viewport a fixed element sits in
 * without changing it, so a sheet at `bottom: 0` ends up behind it and a
 * centred dialog ends up half under it. `use-viewport-bounds.ts` measures what
 * is covered; halving it lifts the dialog into the middle of what is left.
 *
 * Tailwind reads these strings literally, so a class built by interpolation is
 * never generated.
 */
const LAYOUT = {
  dialog: [
    'left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg',
    'top-[calc(50%-var(--viewport-inset-bottom,0px)/2)]',
  ].join(' '),
  sheet: [
    'inset-x-0 mx-auto rounded-t-2xl',
    'bottom-[var(--viewport-inset-bottom,0px)]',
    'pb-[env(safe-area-inset-bottom)]',
    'md:inset-x-auto md:bottom-auto md:left-1/2',
    'md:top-[calc(50%-var(--viewport-inset-bottom,0px)/2)]',
    'md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg md:pb-0',
  ].join(' '),
} as const

/**
 * How large the dialog sets its own chrome.
 *
 * One scale, which is a phone's: the dialog is a sheet there, and it has to
 * fit its head, its fields and its foot into whatever the on-screen keyboard
 * leaves.
 */
const CHROME = {
  head: 'px-5 py-3.5',
  title: 'text-[15px]',
  description: 'mt-0.5 text-xs',
  body: 'px-5 py-4',
  footer: 'px-5 py-3',
} as const

/**
 * What the editorial surface adds to it from `md` up.
 *
 * Internal is a dense working surface, where a dialog is one more pane among
 * several. Customer and Public set their body text larger and their action is
 * a `CtaButton`, beside which a 15px title reads as a caption under a button
 * rather than as the dialog's subject. Only the `md:` half differs, so the two
 * cannot drift apart on a phone.
 */
const EDITORIAL = {
  head: 'md:px-7 md:py-6',
  title: 'md:text-2xl',
  description: 'md:mt-2 md:text-base',
  body: 'md:px-7 md:py-6',
  footer: 'md:px-7 md:py-5',
} as const

type ModalProps = {
  open: boolean
  /** Escape, the overlay, the close button — every way out lands here. */
  onClose: () => void
  /** Heading and accessible name. Omit only where the body opens with one. */
  title?: string
  description?: string
  size?: keyof typeof SIZE
  layout?: keyof typeof LAYOUT
  /** Height and width beyond what `size` sets — a viewer that fills the screen. */
  contentClassName?: string
  /** Lays the footer out other than as two answers at the right edge — a
   *  sheet that puts a quiet reset opposite its confirm. */
  footerClassName?: string
  /** Replaces the body's padding and scrolling, for a body that fills instead. */
  bodyClassName?: string
  /** Pinned to the bottom, outside the scrolling body. */
  footer?: ReactNode
  /** When false, escape, the overlay and the close button are all gone: only
   *  the body's own action may close the dialog. */
  dismissable?: boolean
  /**
   * A panel the dialog opens beside itself, for what the body is consulted
   * against rather than what it is about: a reference, a preview, the record
   * a form is being filled from.
   *
   * Beside the body from `md` up and in its place below, where a dialog is the
   * width of the screen and has no room for a second column.
   */
  aside?: {
    /** What the button in the head says and what heads the panel. A node, so a
     *  caller that wants a count beside the word renders its own. The panel
     *  carries no close of its own: the dialog's one way out is the X in the
     *  corner, which the panel takes over while open. */
    label: ReactNode
    children: ReactNode
  }
  /**
   * What the body is worked beside, and larger than it: the document a form
   * is filled from, with its own zoom and pages. Takes the left of the dialog
   * from `md` up, with the dialog stretched to the height it may have, so the
   * panel is as tall as the screen allows; below `md` it is left out and the
   * body stands alone. Unlike `aside` it is always open and has no head.
   */
  side?: ReactNode
  children: ReactNode
}

/**
 * A dialog.
 *
 * Radix owns the focus trap, the escape key, the scroll lock and the portal.
 * The subtree is never re-parented while the dialog is open, so an input
 * inside it keeps its caret position across a parent render.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  layout = 'dialog',
  contentClassName,
  footerClassName,
  bodyClassName,
  footer,
  dismissable = true,
  aside,
  side,
  children,
}: ModalProps) {
  const editorial = useEditorialSurface()
  const [asideOpen, setAsideOpen] = useState(false)
  const openAside = asideOpen ? aside : undefined
  // Held as state rather than a ref: the surfaces below only learn where to
  // portal once the dialog has mounted, and a ref would not re-render them.
  const [content, setContent] = useState<HTMLDivElement | null>(null)
  const chrome = (part: keyof typeof CHROME) =>
    cn(CHROME[part], editorial && EDITORIAL[part])

  /** The dialog's one way out, which sits in whichever head is at the top
   *  right corner — its own, or the panel's while that is open. */
  const close = dismissable ? (
    <Dialog.Close
      aria-label={COMMON.close}
      className={`rounded-md p-1 text-muted-foreground hover:bg-line-soft ${FOCUS_RING}`}
    >
      <X size={16} />
    </Dialog.Close>
  ) : null

  return (
    <Dialog.Root open={open} onOpenChange={next => !next && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim/40" />
        <Dialog.Content
          ref={setContent}
          // React bubbles through portals: a dialog opened inside a form would
          // otherwise submit that form too.
          onSubmit={event => event.stopPropagation()}
          onEscapeKeyDown={event => !dismissable && event.preventDefault()}
          onPointerDownOutside={event => !dismissable && event.preventDefault()}
          className={cn(
            usePortalThemeClass(),
            'fixed z-50 flex w-full flex-col bg-card shadow-xl outline-none',
            LAYOUT[layout],
            // A side panel wants the width the viewport allows, whatever
            // the body alone would take.
            SIZE[side ? 'full' : size],
            'max-h-[var(--modal-max-height)]',
            openAside && cn('md:flex-row', SIZE_WITH_ASIDE[size]),
            side && 'md:h-[var(--modal-max-height)] md:flex-row',
            // A phone screen with the keyboard up is barely three fields tall,
            // and a pinned head and foot take two of them. So below `md` the
            // dialog scrolls as one: the title scrolls away with the first
            // field, and the action arrives after the last one the way it does
            // on a page. From `md` up there is room to pin both.
            'overflow-y-auto md:overflow-visible',
            contentClassName,
          )}
        >
          {/* Everything the dialog renders, the footer included: a select in a
              footer is portalled just like one in the body. */}
          <PortalContainerScope container={content}>
            {side ? (
              <div className="hidden md:flex md:min-h-0 md:min-w-0 md:flex-[3] md:flex-col md:border-r md:border-border">
                {side}
              </div>
            ) : null}
            <div
              className={cn(
                'flex min-w-0 flex-col md:min-h-0 md:flex-1',
                side && 'md:flex-[2]',
              )}
            >
              {/* Nothing to head a dialog with when it carries neither a title nor
              a way out — the body opens with its own heading there. */}
              {/* `aside` heads it too: its toggle lives here, and a panel with
                  no way to open it is a panel nobody reaches. */}
              {title || description || dismissable || aside ? (
                <div
                  className={cn(
                    'flex flex-row items-start gap-3',
                    chrome('head'),
                  )}
                >
                  <div className="min-w-0 flex-1">
                    {title ? (
                      <Dialog.Title
                        className={cn(
                          'font-semibold text-foreground',
                          chrome('title'),
                        )}
                      >
                        {title}
                      </Dialog.Title>
                    ) : null}
                    {description ? (
                      <Dialog.Description
                        className={cn(
                          'text-muted-foreground',
                          chrome('description'),
                        )}
                      >
                        {description}
                      </Dialog.Description>
                    ) : null}
                  </div>
                  {aside ? (
                    <button
                      type="button"
                      onClick={() => setAsideOpen(open => !open)}
                      aria-expanded={asideOpen}
                      className={cn(
                        'flex shrink-0 flex-row items-center gap-1.5 rounded-md border px-2 py-1 text-xs',
                        FOCUS_RING,
                        asideOpen
                          ? 'border-primary/40 bg-selected text-foreground'
                          : 'border-border text-muted-foreground hover:bg-line-soft',
                      )}
                    >
                      <PanelRight size={13} aria-hidden />
                      {aside.label}
                    </button>
                  ) : null}
                  {openAside ? null : close}
                </div>
              ) : null}

              <div
                className={cn(
                  'flex flex-col md:min-h-0 md:flex-1 md:overflow-y-auto',
                  // Below `md` the panel is the dialog: one column of screen
                  // width cannot hold the body and a second one beside it. The
                  // footer stays — it carries the dialog's action, and the
                  // panel is something the body is consulted against.
                  openAside && 'max-md:hidden',
                  chrome('body'),
                  bodyClassName,
                )}
              >
                {children}
              </div>

              {footer ? (
                <div
                  className={cn(
                    'flex flex-row items-center justify-end gap-2',
                    chrome('footer'),
                    footerClassName,
                  )}
                >
                  {footer}
                </div>
              ) : null}
            </div>

            {openAside ? (
              <aside className="flex min-w-0 flex-col md:min-h-0 md:w-80 md:shrink-0 md:border-l md:border-border">
                <div
                  className={cn(
                    'flex shrink-0 flex-row items-center gap-3 md:border-b md:border-line-soft',
                    chrome('head'),
                  )}
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-foreground">
                    {openAside.label}
                  </span>
                  {close}
                </div>
                {/* Its own scroll, which is the point: the dialog beside it
                    keeps the one it had. */}
                <div
                  className={cn(
                    'flex flex-col gap-2 md:min-h-0 md:flex-1 md:overflow-y-auto',
                    chrome('body'),
                  )}
                >
                  {openAside.children}
                </div>
              </aside>
            ) : null}
          </PortalContainerScope>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
